// bookie-paper-gold-preview — Oltin Sahifa catalog card (bk01).
//
// Redesigned to read as a quiet hand-bound invitation: a warm gold foil
// border wraps the ivory paper inside, with a small monogram medallion
// in place of a photo. The eyebrow announces "TAKLIFNOMA" in roman
// small-caps; names sit in the template's serif; date + venue close
// the card. All colour values resolve from the LayoutCtx so the same
// component works for any paper-gold bookie variant.
import { Photo, type LayoutCtx } from "./_shared";

export function BookiePaperGoldPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, ${accent} 0%, ${shift(accent, 18)} 30%, ${shift(accent, 32)} 55%, ${shift(accent, 18)} 80%, ${accent} 100%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 6px ${bg}, inset 0 0 0 7px ${accent}33`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Round monogram / photo medallion */}
        <div style={{ width: "60%", aspectRatio: "1 / 1", position: "relative", marginBottom: "5%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <>
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: `radial-gradient(circle at 50% 45%, ${accent}22, ${bg})`,
                        }}
                      />
                      <svg viewBox="0 0 80 80" style={{ position: "relative", width: "55%" }}>
                        <g fill="none" stroke={accent} strokeWidth="1.2" opacity="0.7">
                          <circle cx="40" cy="40" r="22" />
                          <path d="M28 42 L36 50 L52 32" />
                        </g>
                      </svg>
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.05, fontWeight: 500, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

// Lighten a hex colour by mixing toward white. Keeps gradient stops
// coherent without forcing the layout to know a second brand colour.
function shift(hex: string, amount: number): string {
  const c = hex.replace("#", "");
  if (c.length < 6) return hex;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const t = (v: number) => Math.min(255, Math.round(v + (255 - v) * (amount / 100)));
  return `rgb(${t(r)},${t(g)},${t(b)})`;
}
